<template>
  <ontos-base v-bind="baseProps" ref="base">
    <div v-if="loaded">
      <!-- Sem subtítulo e sem seção: a tela tem um bloco só, como a home. Nomear duas vezes a
           mesma lista — "Convites" no alto e "Contas" logo abaixo — faria procurar a diferença
           entre as duas palavras. -->
      <ontos-page-header :title="$tt('invitations')" :level="1"/>

      <!-- A fila: pendentes no alto, porque é por eles que se abre esta tela. A ordem vem do
           servidor, para não haver duas respostas sobre qual é ela. -->
      <v-list density="compact">
        <v-list-item v-for="account of accounts" :key="account.user">
          <ontos-person-line :user="account.user" :email="account.email" :name="account.name" :photo="account.photo">
            <!-- O próprio nível aparece como o chip que é quando mexê-lo deixaria o lunedi sem
                 aprovador. A frase abaixo da lista explica a linha sem seletor — botão ausente
                 não explica a própria ausência. -->
            <ontos-colored-value v-if="locked(account)"
                                 :option="accessOption(account.access)" form="chip"/>
            <ontos-colored-select v-else :model-value="account.access" :items="levels"
                                  variant="plain" class="access-select"
                                  @update:model-value="change(account, $event)"/>
          </ontos-person-line>
        </v-list-item>
      </v-list>

      <div v-if="onlyApprover" class="text-body-2 text-grey mt-2">{{ $tt('last-approver-note') }}</div>
    </div>
  </ontos-base>
</template>

<script lang="ts">
import {Component, toNative} from 'vue-facing-decorator'
import RouteComponent from '@/components/shared/RouteComponent'
import ColoredValue from '@/components/shared/ColoredValue.vue'
import ColoredSelect from '@/components/shared/ColoredSelect.vue'
import PersonLine from '@/components/shared/PersonLine.vue'
import {Access, Account, accessName, accessOption, accessOptions} from '@/types/Access'
import {saveErrorMessage} from '@/utils/saveErrors'

/**
 * Quem entra no lunedi, enquanto ele é por convite.
 *
 * Tela de APLICAÇÃO, e não de ambiente: não tem `:system` na rota, estende o `RouteComponent` e
 * por isso não tem `can()` nem `is()` — aquilo é nível de acesso dentro de um ambiente, e isto é admissão
 * no sistema. Quem a protege é o portão do servidor; a tela só desenha.
 *
 * Uma tela só, sem criar nem excluir: contas nascem quando alguém se cadastra, e aqui muda-se o
 * nível delas. Recusar é deixar pendente, e revogar é devolver a pendente — por isso não há
 * botão de recusa, só o seletor.
 */
@Component({
  components: {
    'ontos-colored-value': ColoredValue,
    'ontos-colored-select': ColoredSelect,
    'ontos-person-line': PersonLine
  }
})
class Invitations extends RouteComponent {
  accounts: Account[] = []

  //expostos ao template: função de módulo não é enxergada de lá
  accessOption = accessOption
  levels = accessOptions()

  async load() {
    try {
      this.accounts = await this.$invitations.list()
    } catch (err) {
      this.throwError(this.$tt('error.load-accounts'), err)
    }
  }

  /**
   * O id do único aprovador, ou null havendo mais de um.
   *
   * Sem aprovador não há quem aprove, e ninguém mais entra — o conserto seria um script no banco.
   * **Quem esbarra nisso é sempre a própria pessoa**: só aprovador abre esta tela, e havendo um
   * só, é ele quem está aqui. É a mesma frase do último administrador de um ambiente.
   */
  get onlyApprover(): number | null {
    const approvers = this.accounts.filter(account => account.access === 'approver')
    return approvers.length === 1 ? approvers[0].user : null
  }

  locked(account: Account): boolean {
    return account.user === this.onlyApprover
  }

  async change(account: Account, access: Access) {
    try {
      await this.saving(async () => {
        await this.$invitations.setAccess({user: account.user}, access)
        account.access = access
        this.info(this.$tt('info.access-changed', {email: account.email, access: accessName(access)}))
      })
    } catch (err) {
      //A recusa legível sai do `saveErrorMessage`, dono único de traduzir o `validation` do
      //servidor — é por ele que chega o "o lunedi precisa de ao menos um aprovador"
      this.throwError(saveErrorMessage(err) || this.$tt('error.change-access', {email: account.email}), err)
    }
  }
}

export default toNative(Invitations)
</script>

<style scoped>
/* O seletor acompanha o e-mail na mesma linha em vez de esticar: a lista é de e-mails, que são
   longos, e um select de largura livre empurraria o nome para as reticências. */
.access-select {
  max-width: 180px;
}
</style>
